import CONSTANT from '@/constant'
import { useContext, useState } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { DocumentDispatchContext } from '../HealthClaimDetailPage'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import healthClaimFormAtom from '@/recoil/healthClaimForm/atom'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'

export const CancelButtonHook = () => {
  const { claimFormId } = useParams<{ claimFormId?: string }>()
  const healthClaim = useRecoilValue(healthClaimFormAtom)
  const setHealthClaim = useSetRecoilState(healthClaimFormAtom)
  const navigate = useNavigate()
  const isCreateMode = healthClaim.mode === HealthClaimModeEnum.CREATE
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { resetAllForms, initializeFormsWithData } = useHealthClaimForm()

  const dispatch = useContext(DocumentDispatchContext)

  const redirect = (nextPath: string, currentPath: string, options?: { claimFormId?: string }) => {
    dispatch({ type: 'REMOVE_UPLOAD_ALL' })

    const targetClaimFormId = options?.claimFormId ?? claimFormId

    // If the route requires claimFormId and we don't have one, use base health claim page
    if (nextPath.includes(':claimFormId') && !targetClaimFormId) {
      navigate(CONSTANT.ROUTES.HEALTH_CLAIM_PAGE, {
        state: { referrer: currentPath },
      })
    } else {
      const pathParams = targetClaimFormId ? { claimFormId: targetClaimFormId } : {}
      navigate(generatePath(nextPath, pathParams), {
        state: { referrer: currentPath },
      })
    }
  }

  const handleCancel = () => {
    if (isCreateMode) {
      setIsModalOpen(true)
      return
    }
    // Reset forms/state back to server data before leaving edit page
    try {
      resetAllForms()
      if (healthClaim.data) {
        initializeFormsWithData(healthClaim.data)
      }
    } catch (e) {
      // Ignore errors
    }
    redirect(CONSTANT.ROUTES.HEALTH_CLAIM_VIEW_PAGE, CONSTANT.ROUTES.HEALTH_CLAIM_EDIT_PAGE)
  }

  const handleModalConfirm = () => {
    setIsModalOpen(false)
    // In create mode, clear forms/state before navigating away
    try {
      resetAllForms()
      setHealthClaim((prev) => ({
        ...prev,
        reclaim: false,
        draft: undefined,
        pendingRequestDocuments: [],
      }))
    } catch (e) {
      // Ignore errors
    }
    redirect(CONSTANT.ROUTES.HEALTH_CLAIM_VIEW_PAGE, CONSTANT.ROUTES.HEALTH_CLAIM_CREATE_PAGE, {
      claimFormId: healthClaim.data?.claimFormId,
    })
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  return {
    handleCancel,
    isCreateMode,
    isModalOpen,
    handleModalConfirm,
    handleModalClose,
  }
}
